import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomCalendar from '../../../common/CustomCalendar';
import { User } from '../../../../types/task';
import { WorkStatus } from 'types/project/projectDefaults';
import SearchInput from '../../../common/SearchInput';
import { workTypeUIMap } from '../Dropdowns/TaskTypeDropdown';
import { WorkType } from 'types/project/projectDefaults';
import UserAvatar from '../../../common/UserAvatar';

interface TaskFilterDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: TaskFilterState) => void;
  onClearFilters: () => void;
  assigneeOptions: User[];
  reporterOptions: User[];
  workStatuses: WorkStatus[];
  workTypes: WorkType[];
  currentFilters?: TaskFilterState; // The last-committed filter state
}

export interface TaskFilterState {
  statuses: string[];
  assignees: string | null;
  priorities: string[];
  taskTypes: string[];
  reporters: string | null;
  createdDateRange: {
    from: Date | null;
    to: Date | null;
  };
  dueDateRange: {
    from: Date | null;
    to: Date | null;
  };
}

interface PriorityOption {
  value: string;
  label: string;
  color: string;
  icon: string;
}

const EMPTY_FILTERS: TaskFilterState = {
  statuses: [],
  assignees: null,
  priorities: [],
  taskTypes: [],
  reporters: null,
  createdDateRange: { from: null, to: null },
  dueDateRange: { from: null, to: null },
};

const TaskFilterDropdown: React.FC<TaskFilterDropdownProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  assigneeOptions,
  reporterOptions,
  workStatuses,
  workTypes,
  currentFilters,
}) => {
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string | null>(null);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);
  const [selectedReporters, setSelectedReporters] = useState<string | null>(null);
  const [createdDateRange, setCreatedDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null,
  });
  const [dueDateRange, setDueDateRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null,
  });
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState<string>('');
  const [reporterSearchTerm, setReporterSearchTerm] = useState<string>('');

  // Sync local draft state from currentFilters whenever the panel opens
  React.useEffect(() => {
    if (isOpen) {
      const f = currentFilters || EMPTY_FILTERS;
      setSelectedStatuses(f.statuses);
      setSelectedAssignees(f.assignees);
      setSelectedPriorities(f.priorities);
      setSelectedTaskTypes(f.taskTypes);
      setSelectedReporters(f.reporters);
      setCreatedDateRange(f.createdDateRange);
      setDueDateRange(f.dueDateRange);
      setAssigneeSearchTerm('');
      setReporterSearchTerm('');
    }
  }, [isOpen, currentFilters]);

  // Helper to get UI styles for a status
  const getStatusStyles = (category: string) => {
    switch (category) {
      case 'TODO':
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-900/30 border-gray-600',
        };
      case 'IN_PROGRESS':
        return {
          color: 'text-blue-400',
          bgColor: 'bg-blue-900/30 border-blue-600',
        };
      case 'DONE':
        return {
          color: 'text-green-400',
          bgColor: 'bg-green-900/30 border-green-600',
        };
      default:
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-900/30 border-gray-600',
        };
    }
  };

  const priorityOptions: PriorityOption[] = [
    {
      value: 'HIGH',
      label: 'High',
      color: 'text-red-500',
      icon: '↑',
    },
    {
      value: 'MEDIUM',
      label: 'Medium',
      color: 'text-amber-500',
      icon: '↑↑',
    },
    {
      value: 'LOW',
      label: 'Low',
      color: 'text-green-500',
      icon: '↑↑↑',
    },
  ];

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleAssigneeToggle = (userId: string) => {
    setSelectedAssignees((prev) => (prev === userId ? null : userId));
  };

  const handlePriorityToggle = (priority: string) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  };

  const handleReporterToggle = (userId: string) => {
    setSelectedReporters((prev) => (prev === userId ? null : userId));
  };

  const handleTaskTypeToggle = (typeUuid: string) => {
    setSelectedTaskTypes((prev) =>
      prev.includes(typeUuid)
        ? prev.filter((t) => t !== typeUuid)
        : [...prev, typeUuid]
    );
  };

  const handleCreatedDateRangeChange = (type: 'from' | 'to', date: Date) => {
    setCreatedDateRange((prev) => ({
      ...prev,
      [type]: date,
    }));
  };

  const handleDueDateRangeChange = (type: 'from' | 'to', date: Date) => {
    setDueDateRange((prev) => ({
      ...prev,
      [type]: date,
    }));
  };

  const handleApplyFilters = () => {
    const filters: TaskFilterState = {
      statuses: selectedStatuses,
      assignees: selectedAssignees,
      priorities: selectedPriorities,
      taskTypes: selectedTaskTypes,
      reporters: selectedReporters,
      createdDateRange,
      dueDateRange,
    };

    // If everything is empty, treat as a clear
    const isEmpty =
      filters.statuses.length === 0 &&
      filters.assignees === null &&
      filters.priorities.length === 0 &&
      filters.taskTypes.length === 0 &&
      filters.reporters === null &&
      !filters.createdDateRange.from &&
      !filters.createdDateRange.to &&
      !filters.dueDateRange.from &&
      !filters.dueDateRange.to;

    if (isEmpty) {
      onClearFilters();
    } else {
      onApplyFilters(filters);
    }
    onClose();
  };

  // Only resets the LOCAL draft — does NOT propagate to parent.
  // The clear is committed only when the user clicks "Apply Filters".
  const handleClearAll = () => {
    setSelectedStatuses([]);
    setSelectedAssignees(null);
    setSelectedPriorities([]);
    setSelectedTaskTypes([]);
    setSelectedReporters(null);
    setCreatedDateRange({ from: null, to: null });
    setDueDateRange({ from: null, to: null });
    setAssigneeSearchTerm('');
    setReporterSearchTerm('');
  };

  const hasActiveFilters =
    selectedStatuses.length > 0 ||
    selectedAssignees !== null ||
    selectedPriorities.length > 0 ||
    selectedTaskTypes.length > 0 ||
    selectedReporters !== null ||
    createdDateRange.from ||
    createdDateRange.to ||
    dueDateRange.from ||
    dueDateRange.to;

  const getActiveFilterCount = () => {
    let count =
      selectedStatuses.length +
      (selectedAssignees ? 1 : 0) +
      selectedPriorities.length +
      selectedTaskTypes.length +
      (selectedReporters ? 1 : 0);
    if (createdDateRange.from || createdDateRange.to) count++;
    if (dueDateRange.from || dueDateRange.to) count++;
    return count;
  };

  const getFilteredAssignees = () => {
    return assigneeOptions.filter((user) =>
      user.name.toLowerCase().includes(assigneeSearchTerm.toLowerCase())
    );
  };

  const getFilteredReporters = () => {
    return reporterOptions.filter((user) =>
      user.name.toLowerCase().includes(reporterSearchTerm.toLowerCase())
    );
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className='relative z-50'>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/25' />
        </TransitionChild>

        {/* Full-screen container to center the panel */}
        <div className='fixed inset-0 flex w-screen items-start justify-center p-4'>
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95 translate-y-4'
            enterTo='opacity-100 scale-100 translate-y-0'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100 translate-y-0'
            leaveTo='opacity-0 scale-95 translate-y-4'
          >
            <DialogPanel className='w-full max-w-md transform overflow-hidden rounded-lg bg-primary-intemediate border border-primary-subtle shadow-xl transition-all mt-16'>
              {/* Header */}
              <div className='px-4 py-3 border-b border-primary-intemediate flex items-center justify-between'>
                <h3 className='text-lg font-semibold text-neutral'>
                  Filter Tasks
                </h3>
                <div className='flex items-center gap-3'>
                  <AnimatePresence>
                    {hasActiveFilters && (
                      <motion.span
                        className='bg-blue-500 text-xs px-2 py-1 rounded-full text-white'
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 25,
                        }}
                      >
                        {getActiveFilterCount()} active
                      </motion.span>
                    )}
                  </AnimatePresence>
                  <button
                    onClick={onClose}
                    className='text-neutral-medium hover:text-neutral transition-colors'
                  >
                    <FaTimes size={16} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className='p-4 space-y-6 max-h-96 overflow-y-auto'>
                {/* Status Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Status
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {workStatuses.map((status) => {
                      const styles = getStatusStyles(status.workStatusCategory);
                      return (
                        <motion.button
                          key={status.workStatusUuid}
                          onClick={() => handleStatusToggle(status.workStatusUuid)}
                          className={`px-3 py-2 text-xs rounded-full border transition-all duration-200 ${
                            selectedStatuses.includes(status.workStatusUuid)
                              ? `${styles.bgColor} ${styles.color} border-opacity-100`
                              : 'bg-[#6B728066] text-neutral border-primary-subtle hover:bg-primary-subtle hover:text-gray-300'
                          }`}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {status.name}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Assignee Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Assignee
                  </h4>
                  <div className='space-y-3'>
                    <SearchInput
                      value={assigneeSearchTerm}
                      onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                      placeholder='Search team members...'
                      className='!bg-primary'
                    />
                    <div className='flex flex-wrap gap-2 max-h-24 overflow-y-auto'>
                      {selectedAssignees && (
                        (() => {
                          const user = assigneeOptions.find(
                            (u) => u.id === selectedAssignees
                          );
                          if (!user) return null;
                          return (
                            <motion.div
                              key={selectedAssignees}
                              className='flex items-center gap-2 bg-blue-900/30 border border-blue-600 rounded-full px-3 py-1'
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                            >
                              <UserAvatar 
                                name={user.name} 
                                profileImageURL={user.profileImageURL || user.avatar} 
                                className="w-4 h-4 text-[8px]" 
                              />
                              <span className='text-xs text-blue-400'>
                                {user.name}
                              </span>
                              <button
                                onClick={() => handleAssigneeToggle(selectedAssignees)}
                                className='text-blue-400 hover:text-blue-300'
                              >
                                <FaTimes size={8} />
                              </button>
                            </motion.div>
                          );
                        })()
                      )}
                    </div>
                    <div className='space-y-1 max-h-32 overflow-y-auto bg-primary p-1'>
                      {getFilteredAssignees().map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleAssigneeToggle(user.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedAssignees === user.id
                              ? 'bg-blue-900/30 text-blue-400'
                              : 'hover:bg-primary-subtle text-neutral-medium hover:text-gray-300'
                          }`}
                        >
                          <UserAvatar 
                            name={user.name} 
                            profileImageURL={user.profileImageURL || user.avatar} 
                            className="w-5 h-5 text-[10px]" 
                          />
                          <span className='truncate'>{user.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Priority Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Priority
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {priorityOptions.map((priority) => (
                      <motion.button
                        key={priority.value}
                        onClick={() => handlePriorityToggle(priority.value)}
                        className={`px-3 py-2 rounded-lg bg-primary text-xs font-semibold border transition-all duration-200 flex items-center gap-2 ${
                          selectedPriorities.includes(priority.value)
                            ? `bg-blue-900/30 border-blue-600 text-blue-400`
                            : 'bg-primary-light/50 text-neutral-medium border-primary-subtle hover:bg-primary-subtle hover:text-gray-300'
                        }`}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        title={priority.label}
                      >
                        <span className={priority.color}>{priority.icon}</span> {priority.label}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Task Type Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Task Type
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {workTypes.map((type) => {
                      const ui = workTypeUIMap[type.workTypeCategory];
                      const isSelected = selectedTaskTypes.includes(type.workTypeUuid);
                      return (
                        <motion.button
                          key={type.workTypeUuid}
                          onClick={() => handleTaskTypeToggle(type.workTypeUuid)}
                          className={`px-3 py-2 text-xs rounded-full border transition-all duration-200 flex items-center gap-2 ${
                            isSelected
                              ? 'bg-blue-900/30 text-blue-400 border-blue-600'
                              : 'bg-[#6B728066] text-neutral border-primary-subtle hover:bg-primary-subtle hover:text-gray-300'
                          }`}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <span className={ui ? ui.color : 'text-neutral'}>
                            {ui ? ui.icon : null}
                          </span>
                          {type.name}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Created Date Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Created Date
                  </h4>
                  <div className='grid grid-cols-2 gap-3'>
                    <div className='flex flex-col'>
                      <label className='text-xs text-gray-400 mb-1'>From</label>
                      <CustomCalendar
                        value={createdDateRange.from}
                        onChange={(date) =>
                          handleCreatedDateRangeChange('from', date)
                        }
                        placeholder='mm/dd/yyyy'
                        dropdownWidth={280}
                        className='text-sm bg-primary border border-primary-subtle h-[38px] w-full'
                        maxDate={createdDateRange.to || undefined}
                      />
                    </div>
                    <div className='flex flex-col'>
                      <label className='text-xs text-neutral-medium mb-1'>
                        To
                      </label>
                      <CustomCalendar
                        value={createdDateRange.to}
                        onChange={(date) =>
                          handleCreatedDateRangeChange('to', date)
                        }
                        placeholder='mm/dd/yyyy'
                        dropdownWidth={280}
                        minDate={createdDateRange.from || undefined}
                        className='text-sm bg-primary border border-primary-subtle h-[38px] w-full'
                      />
                    </div>
                  </div>
                </div>

                {/* Due Date Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Due Date
                  </h4>
                  <div className='grid grid-cols-2 gap-3'>
                    <div className='flex flex-col'>
                      <label className='text-xs text-gray-400 mb-1'>From</label>
                      <CustomCalendar
                        value={dueDateRange.from}
                        onChange={(date) =>
                          handleDueDateRangeChange('from', date)
                        }
                        placeholder='mm/dd/yyyy'
                        dropdownWidth={280}
                        className='text-sm bg-primary border border-primary-subtle h-[38px] w-full'
                        maxDate={dueDateRange.to || undefined}
                      />
                    </div>
                    <div className='flex flex-col'>
                      <label className='text-xs text-gray-400 mb-1'>To</label>
                      <CustomCalendar
                        value={dueDateRange.to}
                        onChange={(date) =>
                          handleDueDateRangeChange('to', date)
                        }
                        placeholder='mm/dd/yyyy'
                        dropdownWidth={280}
                        minDate={dueDateRange.from || undefined}
                        className='text-sm bg-primary border border-primary-subtle h-[38px] w-full'
                      />
                    </div>
                  </div>
                </div>

                {/* Reporter Filter */}
                <div>
                  <h4 className='text-sm font-medium text-gray-300 mb-3'>
                    Reporter
                  </h4>
                  <div className='space-y-3'>
                    <SearchInput
                      value={reporterSearchTerm}
                      onChange={(e) => setReporterSearchTerm(e.target.value)}
                      placeholder='Search reporters...'
                      className='!bg-primary'
                    />
                    <div className='flex flex-wrap gap-2 max-h-24 overflow-y-auto'>
                      {selectedReporters && (
                        (() => {
                          const user = reporterOptions.find(
                            (u) => u.id === selectedReporters
                          );
                          if (!user) return null;
                          return (
                            <motion.div
                              key={selectedReporters}
                              className='flex items-center gap-2 bg-purple-900/30 border border-purple-600 rounded-full px-3 py-1'
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                            >
                              <UserAvatar 
                                name={user.name} 
                                profileImageURL={user.profileImageURL || user.avatar} 
                                className="w-4 h-4 text-[8px]" 
                              />
                              <span className='text-xs text-purple-400'>
                                {user.name}
                              </span>
                              <button
                                onClick={() => handleReporterToggle(selectedReporters)}
                                className='text-purple-400 hover:text-purple-300'
                              >
                                <FaTimes size={8} />
                              </button>
                            </motion.div>
                          );
                        })()
                      )}
                    </div>
                    <div className='space-y-1 max-h-32 overflow-y-auto bg-primary p-1'>
                      {getFilteredReporters().map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleReporterToggle(user.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedReporters === user.id
                              ? 'bg-purple-900/30 text-purple-400'
                              : 'hover:bg-primary-subtle text-gray-400 hover:text-gray-300'
                          }`}
                        >
                          <UserAvatar 
                            name={user.name} 
                            profileImageURL={user.profileImageURL || user.avatar} 
                            className="w-5 h-5 text-[10px]" 
                          />
                          <span className='truncate'>{user.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className='px-4 py-3 border-t border-primary-subtle flex justify-between items-center'>
                <motion.button
                  onClick={handleClearAll}
                  className='px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50'
                  disabled={!hasActiveFilters}
                  whileHover={hasActiveFilters ? { scale: 1.05 } : {}}
                  whileTap={hasActiveFilters ? { scale: 0.95 } : {}}
                >
                  Clear All
                </motion.button>
                <div className='flex gap-2'>
                  <motion.button
                    onClick={onClose}
                    className='px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors'
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    onClick={handleApplyFilters}
                    className='px-4 py-2 button-active text-sm rounded-lg'
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Apply Filters
                  </motion.button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaskFilterDropdown;
